import { jobKindLabel } from "@/components/jobs/job-elapsed-timer"
import { visitCanvasGeneration } from "@/lib/canvas-generation-url"
import {
  canOpenCanvasGeneration,
  canOpenCharacter,
  canOpenCharactersScaleSheet,
  canOpenLocation,
  canOpenLocationSceneFace,
  canOpenMixedScaleSheet,
  canOpenProp,
  canOpenPropsScaleSheet,
  canOpenSketchEdit,
  canvasJobDisplayNumber,
  canvasJobSubjectPrefix,
  canvasVisitScope,
  parseCharacterJobSubjectLabel,
  parseJobSubjectLabel,
  previewTabForJobKind,
  resolveJobCharacterId,
  resolveJobLocationId,
  resolveJobPropId,
  resolveMixedScaleSheetShotId,
  resolveSketchEditId,
  resolveSketchFileName,
  visitCharacter,
  visitCharactersScaleSheet,
  visitLocation,
  visitProp,
  visitPropsScaleSheet,
  type JobSubjectLabelSource,
} from "@/lib/job-subject-label"
import { cn } from "@/lib/utils"
import { useProjectPermissions } from "@/hooks/use-permissions"
import { useTranslation } from "@/hooks/use-translation"
import { useShotPanelStore } from "@/stores/shot-panel-store"
import { useSketchJobResultStore } from "@/stores/sketch-job-result-store"

export function JobSubjectLabel({
  job,
  projectId,
  tone = "accent",
  weight = "medium",
}: {
  job: JobSubjectLabelSource
  projectId: number
  /** В панели задач подпись акцентная, в карточках ленты — обычным текстом. */
  tone?: "accent" | "plain"
  /** В боковом меню строки набраны как у заметок и агентов — без medium. */
  weight?: "medium" | "normal"
}) {
  const { t } = useTranslation(["jobs", "workspace"])
  const projectPermissions = useProjectPermissions()
  const openShotPanel = useShotPanelStore((s) => s.openShotPanel)
  const openSketchJobResult = useSketchJobResultStore((s) => s.openSketchJobResult)
  const kindLabel = jobKindLabel(job.job_kind)
  const shotId = job.shot_ids?.length === 1 ? job.shot_ids[0] : null
  const parsed = shotId && job.subject_label ? parseJobSubjectLabel(job.subject_label) : null
  const canvasOpenable = canOpenCanvasGeneration(job)
  const sketchOpenable = canOpenSketchEdit(job, {
    canEditContent: projectPermissions?.can_edit_content === true,
  })
  const sketchFileName = job.job_kind === "sketch_edit" ? resolveSketchFileName(job) : null
  const canvasSketchOpenable = job.job_kind === "sketch_edit" && canvasOpenable
  const characterId = resolveJobCharacterId(job)
  const characterOpenable = canOpenCharacter(job)
  const parsedCharacter =
    characterOpenable && job.subject_label ? parseCharacterJobSubjectLabel(job.subject_label) : null
  const propId = resolveJobPropId(job)
  const propOpenable = canOpenProp(job)
  const parsedProp = propOpenable && job.subject_label ? parseCharacterJobSubjectLabel(job.subject_label) : null
  const locationId = resolveJobLocationId(job)
  const locationOpenable = canOpenLocation(job)
  const parsedLocation = locationOpenable && job.subject_label ? parseCharacterJobSubjectLabel(job.subject_label) : null
  const faceOpenable = canOpenLocationSceneFace(job)
  const parsedFace =
    faceOpenable && job.subject_label && !locationOpenable ? parseCharacterJobSubjectLabel(job.subject_label) : null
  const charactersScaleOpenable = canOpenCharactersScaleSheet(job)
  const propsScaleOpenable = canOpenPropsScaleSheet(job)
  const mixedScaleOpenable = canOpenMixedScaleSheet(job)
  const mixedScaleShotId = resolveMixedScaleSheetShotId(job)

  const primaryTone = cn(
    "text-left",
    weight === "medium" && "font-medium",
    tone === "plain"
      ? (weight === "medium" ? "text-sm" : "text-[13px] leading-5") + " text-ui-text-primary"
      : "text-ui-accent-text",
  )
  const shotLinkTone = cn("inline-block max-w-full text-left underline-offset-2 hover:underline text-ui-link")
  // В карточках ленты тип генерации уже читается в заголовке — вторая строка была бы дублем.
  const kindCaption = tone === "plain" ? null : <span className="text-[10px] text-ui-text-muted">{kindLabel}</span>

  const openShot = () => {
    if (!shotId) return
    openShotPanel({
      projectId,
      shotId,
      view: "preview",
      previewTab: previewTabForJobKind(job.job_kind, job.sketch_shot_tab),
    })
  }

  const openMixedScaleShot = () => {
    if (mixedScaleShotId == null) return
    openShotPanel({
      projectId,
      shotId: mixedScaleShotId,
      view: "preview",
      previewTab: "video",
      openMixedScaleSheet: true,
    })
  }

  const openCanvas = () => {
    if (!canvasOpenable || job.canvas_id == null || job.canvas_generation_id == null) return
    visitCanvasGeneration(canvasVisitScope(job, projectId), { id: job.canvas_id }, { id: job.canvas_generation_id })
  }

  const openSketch = () => {
    const editId = resolveSketchEditId(job)
    if (!sketchOpenable || editId == null) return
    openSketchJobResult({
      projectId,
      editId,
      fileName: sketchFileName || "sketch.png",
      presentation: "view",
    })
  }

  const openFaceResult = () => {
    const url = job.preview_media_url?.trim()
    if (!faceOpenable || !url) return
    openSketchJobResult({
      projectId,
      sourceUrl: url,
      downloadUrl: url,
      fileName: "face.png",
      presentation: "view",
    })
  }

  const openCharacter = () => {
    if (!characterId) return
    visitCharacter(projectId, characterId)
  }

  const openProp = () => {
    if (!propId) return
    visitProp(projectId, propId)
  }

  const openLocation = () => {
    if (!locationId) return
    visitLocation(projectId, locationId)
  }

  const openCharactersScaleSheet = () => {
    visitCharactersScaleSheet(projectId)
  }

  const openPropsScaleSheet = () => {
    visitPropsScaleSheet(projectId)
  }

  if (
    job.job_kind === "sketch_edit" &&
    sketchFileName &&
    shotId == null &&
    !canvasSketchOpenable &&
    !characterOpenable &&
    !propOpenable &&
    !locationOpenable
  ) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className={primaryTone}>
          {t("sketchSubjectPrefix")}:{" "}
          {sketchOpenable ? (
            <button type="button" className={shotLinkTone} onClick={openSketch}>
              {sketchFileName}
            </button>
          ) : (
            <span>{sketchFileName}</span>
          )}
        </div>
        {kindCaption}
      </div>
    )
  }

  if (!job.subject_label) {
    return (
      <div className="min-w-0">
        <span className={primaryTone}>{kindLabel}</span>
      </div>
    )
  }

  if (parsed) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className={primaryTone}>
          {parsed.prefix}:{" "}
          <button type="button" className={shotLinkTone} onClick={openShot}>
            {parsed.shotLabel}
          </button>
        </div>
        {kindCaption}
      </div>
    )
  }

  if (canvasOpenable) {
    const prefix = canvasJobSubjectPrefix(job, t("canvas.headTitle", { ns: "workspace" }))
    const displayNumber = canvasJobDisplayNumber(job)!

    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className={primaryTone}>
          {prefix}:{" "}
          <button type="button" className={shotLinkTone} onClick={openCanvas}>
            #{displayNumber}
          </button>
        </div>
        {kindCaption}
      </div>
    )
  }

  if (parsedCharacter) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className={primaryTone}>
          {parsedCharacter.prefix}:{" "}
          <button type="button" className={shotLinkTone} onClick={openCharacter}>
            {parsedCharacter.characterName}
          </button>
          {parsedCharacter.suffix ? ` — ${parsedCharacter.suffix}` : null}
        </div>
        {kindCaption}
      </div>
    )
  }

  if (parsedProp) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className={primaryTone}>
          {parsedProp.prefix}:{" "}
          <button type="button" className={shotLinkTone} onClick={openProp}>
            {parsedProp.characterName}
          </button>
          {parsedProp.suffix ? ` — ${parsedProp.suffix}` : null}
        </div>
        {kindCaption}
      </div>
    )
  }

  if (parsedLocation) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className={primaryTone}>
          {parsedLocation.prefix}:{" "}
          <button type="button" className={shotLinkTone} onClick={openLocation}>
            {parsedLocation.characterName}
          </button>
          {parsedLocation.suffix ? ` — ${parsedLocation.suffix}` : null}
        </div>
        {kindCaption}
      </div>
    )
  }

  if (parsedFace) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className={primaryTone}>
          {parsedFace.prefix}:{" "}
          <button type="button" className={shotLinkTone} onClick={openFaceResult}>
            {parsedFace.characterName}
          </button>
          {parsedFace.suffix ? ` — ${parsedFace.suffix}` : null}
        </div>
        {kindCaption}
      </div>
    )
  }

  if (faceOpenable && job.subject_label) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-0.5 overflow-hidden">
        <button
          type="button"
          className={cn(primaryTone, shotLinkTone, "block w-full truncate text-left")}
          onClick={openFaceResult}
          title={job.subject_label}
        >
          {job.subject_label}
        </button>
        {kindCaption}
      </div>
    )
  }

  if (charactersScaleOpenable) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-0.5 overflow-hidden">
        <button
          type="button"
          className={cn(primaryTone, shotLinkTone, "block w-full truncate text-left")}
          onClick={openCharactersScaleSheet}
          title={job.subject_label ?? undefined}
        >
          {job.subject_label}
        </button>
        {kindCaption}
      </div>
    )
  }

  if (propsScaleOpenable) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-0.5 overflow-hidden">
        <button
          type="button"
          className={cn(primaryTone, shotLinkTone, "block w-full truncate text-left")}
          onClick={openPropsScaleSheet}
          title={job.subject_label ?? undefined}
        >
          {job.subject_label}
        </button>
        {kindCaption}
      </div>
    )
  }

  if (mixedScaleOpenable) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-0.5 overflow-hidden">
        <button
          type="button"
          className={cn(primaryTone, shotLinkTone, "block w-full truncate text-left")}
          onClick={openMixedScaleShot}
          title={job.subject_label ?? undefined}
        >
          {job.subject_label}
        </button>
        {kindCaption}
      </div>
    )
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-0.5 overflow-hidden">
      <span className={cn(primaryTone, "block w-full truncate")} title={job.subject_label ?? undefined}>
        {job.subject_label}
      </span>
      {kindCaption}
    </div>
  )
}
